/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization, type DateRange } from "@lemmonhq/uikit";
import type { CatalogEntry, DataRoomTab, ReportTimeWindow } from "../state";
import type { ExportPresentation, ExportRun } from "../exports";
import { ReportFilters, type FilterOptions } from "./ReportFilters";
import { DownloadMenu } from "./DownloadMenu";

type Props = {
  entry?: CatalogEntry;
  tab?: DataRoomTab;
  options?: FilterOptions;
  timeWindow?: ReportTimeWindow;
  /** Range shared with the other report pages, used for its preset type. */
  range?: DateRange;
  refreshing: boolean;
  run?: ExportRun;
  onFilterChange: (patch: Record<string, unknown>) => void;
  onRangeChange: (range: DateRange) => void;
  onRefresh: () => void;
  onStart: (kind: ExportPresentation) => void;
  onReset: () => void;
  onDownload: (id: string) => void;
};

/** Persistent toolbar shown below the Data Room tabs, including blank tabs. */
export const DataRoomToolbar = defineComponent<Props>({
  name: "DataRoomToolbar",
  props: [
    "entry",
    "tab",
    "options",
    "timeWindow",
    "range",
    "refreshing",
    "run",
    "onFilterChange",
    "onRangeChange",
    "onRefresh",
    "onStart",
    "onReset",
    "onDownload",
  ],
  setup(props) {
    const { t } = useLocalization();

    return () => {
      const hasReport = !!props.entry && !!props.tab?.definitionId;

      return (
        // Tighter on mobile (min-h dropped, padding halved) so the filter strip
        // costs less of a phone screen, and an opaque background with a solid
        // border so it reads as a distinct bar rather than a translucent haze
        // over whatever scrolls beneath it.
        <div class="sticky top-0 flex flex-wrap items-center justify-between gap-2 border-b bg-base-200 px-3 py-2 shadow-sm sm:min-h-[4.5rem] sm:gap-3 sm:px-6 sm:py-3">
          {hasReport ? (
            <>
              <ReportFilters
                entry={props.entry!}
                parameters={props.tab!.parameters}
                options={props.options}
                timeWindow={props.timeWindow}
                range={props.range}
                onChange={props.onFilterChange}
                onRangeChange={props.onRangeChange}
              />
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="btn btn-sm btn-square bg-base-100 text-base-content"
                  disabled={props.refreshing}
                  title={t("reports.dataroom.refresh")}
                  aria-label={t("reports.dataroom.refresh")}
                  onClick={props.onRefresh}
                >
                  <span
                    class={`material-symbols-outlined text-base ${
                      props.refreshing ? "animate-spin" : ""
                    }`}
                  >
                    refresh
                  </span>
                </button>
                <DownloadMenu
                  presentations={props.entry!.presentations}
                  run={props.run}
                  onStart={props.onStart}
                  onReset={props.onReset}
                  onDownload={props.onDownload}
                />
              </div>
            </>
          ) : (
            <div class="flex min-h-8 items-center gap-2 text-sm text-base-content/70">
              <span class="material-symbols-outlined text-base">tune</span>
              <span>{t("reports.dataroom.toolbar_empty")}</span>
            </div>
          )}
        </div>
      );
    };
  },
});
